import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { ApiError, api, setUnauthorizedHandler } from '../api/client';
import type { Action, Me } from '../api/types';

interface AuthValue {
  me: Me | null;
  loading: boolean;
  can: (menu: string, action?: Action) => boolean;
  login: (email: string, password: string) => Promise<Me>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);
const ME_KEY = ['me'];

/** Permission check against a /auth/me payload; super admins ('all') may do everything. */
export function canDo(me: Me | null, menu: string, action: Action = 'view'): boolean {
  if (!me) return false;
  if (me.permissions === 'all') return true;
  return me.permissions[menu]?.[action] === true;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const meQuery = useQuery({
    queryKey: ME_KEY,
    queryFn: async (): Promise<Me | null> => {
      try {
        return await api.get<Me>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null; // not signed in is a normal state
        throw e;
      }
    },
    staleTime: Infinity,
    retry: false,
  });

  // A 401 anywhere means the session ended: drop cached data and show the login page.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      qc.setQueryData(ME_KEY, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    });
    return () => setUnauthorizedHandler(undefined);
  }, [qc]);

  const loginMutation = useMutation({
    mutationFn: (v: { email: string; password: string }) => api.post<Me>('/auth/login', v),
    onSuccess: (me) => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      qc.setQueryData(ME_KEY, me);
    },
  });

  const me = meQuery.data ?? null;

  const value = useMemo<AuthValue>(
    () => ({
      me,
      loading: meQuery.isPending,
      can: (menu, action = 'view') => canDo(me, menu, action),
      login: (email, password) => loginMutation.mutateAsync({ email, password }),
      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch {
          // Best effort: even if the server is unreachable the user must end up signed out locally
          // (the session cookie is httpOnly and expires on its own).
        } finally {
          qc.setQueryData(ME_KEY, null);
          qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
        }
      },
      refresh: async () => {
        await qc.invalidateQueries({ queryKey: ME_KEY });
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me, meQuery.isPending, qc],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}

/** Convenience: `const canEdit = useCan('agents', 'edit')`. */
export function useCan(menu: string, action: Action = 'view'): boolean {
  const { me } = useAuth();
  return canDo(me, menu, action);
}
